import * as R from 'Router/routes';
import { Application } from 'hooks/webapi/useApplication';
import { Client } from 'hooks/webapi/useClient';
import {
  INSURANCE_POLICY,
  INSURANCE_POLICY_ENABLED_ONCE,
  INSURANCE_POLICY_SKIPPED,
} from 'utils/constants';
import { useApplication, useClient, useLatestLoan } from 'hooks/webapi';
import { useNavigate } from 'react-router-dom';
import useBundle from 'hooks/useBundle';

export const useInsurancePolicy = () => {
  const navigate = useNavigate();
  const { client } = useClient();
  const { application } = useApplication();
  const { latestLoan } = useLatestLoan();
  const { isBundleAvailable } = useBundle();

  const getIsInsuranceAvailable = ({
    applicationFetched,
    clientFetched,
  }: {
    applicationFetched?: Application | null | undefined;
    clientFetched?: Client | null | undefined;
  } = {}) => {
    const clientToCheck = clientFetched || client;
    const applicationToCheck = applicationFetched || application;
    return Boolean(
      clientToCheck &&
        clientToCheck?.loansCount > 0 &&
        !isBundleAvailable &&
        applicationToCheck?.insurancePrice &&
        !latestLoan,
    );
  };

  const navigateToBestOfferOrInsuranceScreen = (
    applicationFetched?: Application | null | undefined,
  ) => {
    const insuranceAvailable = getIsInsuranceAvailable({
      applicationFetched: applicationFetched || application,
    });
    const screen = insuranceAvailable ? R.INSURANCE_POLICY : R.BEST_OFFER;
    navigate(screen);
  };

  const isInsuranceAvailable = getIsInsuranceAvailable({
    applicationFetched: application,
  });

  const enableInsurancePolicy = () => {
    sessionStorage.setItem(INSURANCE_POLICY, 'true');
    sessionStorage.setItem(INSURANCE_POLICY_ENABLED_ONCE, 'true');
  };

  const disableInsurancePolicy = () => {
    sessionStorage.removeItem(INSURANCE_POLICY);
  };

  const skipInsurancePolicy = () => {
    disableInsurancePolicy();
    sessionStorage.setItem(INSURANCE_POLICY_SKIPPED, 'true');
  };

  const clearInsurancePolicySkipped = () => {
    sessionStorage.removeItem(INSURANCE_POLICY_SKIPPED);
  };

  const getIsInsurancePolicySkipped = () => {
    return sessionStorage.getItem(INSURANCE_POLICY_SKIPPED) === 'true';
  };

  const getIsInsurancePolicyEnabled = () => {
    const insuranceApplied = Boolean(
      application?.loanApplicationEnhancements?.includes('INSURED'),
    );
    return (
      sessionStorage.getItem(INSURANCE_POLICY) === 'true' || insuranceApplied
    );
  };

  const navigateToNextScreen = () => {
    navigate(
      application?.resolutionDetail === 'NEW_OFFER'
        ? R.BEST_OFFER
        : R.LOAN_CONFIRM,
    );
  };

  const isInsurancePolicyEnabled = getIsInsurancePolicyEnabled();

  return {
    enableInsurancePolicy,
    disableInsurancePolicy,
    skipInsurancePolicy,
    clearInsurancePolicySkipped,
    getIsInsurancePolicySkipped,
    isInsuranceAvailable,
    getIsInsuranceAvailable,
    navigateToNextScreen,
    navigateToBestOfferOrInsuranceScreen,
    getIsInsurancePolicyEnabled,
    isInsurancePolicyEnabled,
  };
};
